import React, { useEffect, useState } from "react";
import { Card, Badge, Spinner, Alert, Form, Button, Modal } from "react-bootstrap";
import { useEngineeringTasks } from "../../functions/useEngineeringTasks";
import useRealtimeDraft from '../../realtime/useRealtimeDraft';
import { useSelector } from "react-redux";
import { format, isPast, isWithinInterval, addDays } from "date-fns";
import { formatDateForForm } from "../../utils/dateUtils";
import "./TaskManagement.css";

const UserTasks = () => {
  const currentUser = useSelector((state) => state.user?.userDetails?.name || '');
  const { 
    tasks, 
    comments,
    loading, 
    error, 
    fetchUserTasks,
    updateTask,
    fetchTaskComments,
    createComment,
    createTask
  } = useEngineeringTasks({ assignedTo: currentUser, commentsEnabled: true });
  
  const [sortBy, setSortBy] = useState("DueDate");
  const [sortOrder, setSortOrder] = useState("asc");
  
  // Get unique initiative values for dropdown
  const uniqueInitiatives = React.useMemo(() => {
    const initiatives = tasks
      .map(task => task.Initiative)
      .filter(initiative => initiative && initiative.trim() !== "")
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort();
    return initiatives;
  }, [tasks]);
  const [expandedTask, setExpandedTask] = useState(null);
  const [filters, setFilters] = useState({
    priority: null,
    status: null,
    showCompleted: false
  });
  const [newComment, setNewComment] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  useRealtimeDraft(showCreateModal || Object.values(newComment).some(value => Boolean(value?.trim())) || Object.values(commentLoading).some(Boolean));
  const [formData, setFormData] = useState({
    Title: "",
    Description: "",
    Duration: "",
    Priority: "Medium",
    Status: "Not Started",
    DueDate: "",
    DateAssigned: "",
    Effort: "",
    Progress: 0,
    Notes: "",
    StartDate: formatDateForForm(new Date()),
    Initiative: "",
    InitiatedBy: "",
  });

  useEffect(() => {
    if (currentUser) {
      fetchUserTasks(currentUser);
    }
  }, [currentUser, fetchUserTasks]);

  // Filter and sort tasks
  const filteredAndSortedTasks = React.useMemo(() => {
    let filtered = [...tasks];

    // Apply filters
    if (!filters.showCompleted) {
      filtered = filtered.filter(task => task.Status !== "Completed");
    }

    if (filters.priority) {
      filtered = filtered.filter(task => task.Priority === filters.priority);
    }

    if (filters.status) {
      filtered = filtered.filter(task => task.Status === filters.status);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle null/undefined values
      if (!aVal && !bVal) return 0;
      if (!aVal) return sortOrder === "asc" ? 1 : -1;
      if (!bVal) return sortOrder === "asc" ? -1 : 1;

      // Handle dates
      if (sortBy === "DueDate" || sortBy === "StartDate" || sortBy === "DateCreated") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      }

      // Handle numbers
      if (sortBy === "Priority") {
        const priorityOrder = { "Low": 1, "Medium": 2, "High": 3, "Critical": 4 };
        aVal = priorityOrder[aVal] || 0;
        bVal = priorityOrder[bVal] || 0;
      }

      if (sortBy === "Progress") {
        aVal = parseInt(aVal) || 0;
        bVal = parseInt(bVal) || 0;
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [tasks, filters, sortBy, sortOrder]);

  const getDueDateStatus = (dueDate) => {
    if (!dueDate) return { text: "No due date", variant: "secondary", isOverdue: false };

    const date = new Date(dueDate);
    const today = new Date();
    
    if (isPast(date) && !isToday(date)) {
      return { text: "Overdue", variant: "danger", isOverdue: true };
    }
    
    if (isToday(date)) {
      return { text: "Due Today", variant: "warning", isOverdue: false };
    }
    
    if (isWithinInterval(date, { start: today, end: addDays(today, 3) })) {
      return { text: "Due Soon", variant: "warning", isOverdue: false };
    }
    
    return { text: format(date, "MMM dd, yyyy"), variant: "info", isOverdue: false };
  };

  const isToday = (date) => {
    const today = new Date();
    const checkDate = new Date(date);
    return checkDate.getDate() === today.getDate() &&
           checkDate.getMonth() === today.getMonth() &&
           checkDate.getFullYear() === today.getFullYear();
  };

  const getPriorityBadge = (priority) => {
    const { PRIORITY_BADGE_VARIANTS } = require('../../utils/priorityBadgeConfig');
    return <Badge bg={PRIORITY_BADGE_VARIANTS[priority] || "secondary"}>{priority}</Badge>;
  };

  const getStatusBadge = (status) => {
    const variants = {
      "Not Started": "secondary",
      "In Progress": "primary",
      "On Hold": "warning",
      "Completed": "success",
      "Cancelled": "danger"
    };
    return <Badge bg={variants[status] || "secondary"}>{status}</Badge>;
  };

  const handleUpdateProgress = async (taskId, newProgress) => {
    try {
      // PATCH uses PascalCase, Progress as string
      await updateTask(taskId, { ID: taskId, Progress: String(newProgress) });
      fetchUserTasks(currentUser);
    } catch (err) {
      console.error("Error updating progress:", err);
    }
  };

  const handleUpdateStatus = async (taskId, newStatus) => {
    try {
      // PATCH uses PascalCase
      await updateTask(taskId, { ID: taskId, Status: newStatus });
      fetchUserTasks(currentUser);
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  const toggleTaskExpansion = (taskId) => {
    const isExpanding = expandedTask !== taskId;
    setExpandedTask(isExpanding ? taskId : null);
    
    // Fetch comments when expanding a task
    if (isExpanding) {
      fetchTaskComments(taskId);
    }
  };

  const handleAddComment = async (taskId) => {
    const commentText = newComment[taskId]?.trim();
    if (!commentText) return;

    try {
      setCommentLoading({ ...commentLoading, [taskId]: true });
      await createComment({
        taskID: taskId,
        comment: commentText,
        author: currentUser
      });
      
      // Clear the input and refresh comments
      setNewComment({ ...newComment, [taskId]: "" });
      await fetchTaskComments(taskId);
    } catch (err) {
      console.error("Error adding comment:", err);
    } finally {
      setCommentLoading({ ...commentLoading, [taskId]: false });
    }
  };

  const handleCommentChange = (taskId, value) => {
    setNewComment({ ...newComment, [taskId]: value });
  };

  const handleOpenCreateModal = () => {
    console.log("Button clicked, currentUser:", currentUser); // Debug log
    
    // Allow modal to open even if currentUser is empty - we'll handle it in form submission
    console.log("Opening create modal for user:", currentUser || "Unknown"); // Debug log
    setFormData({
      Title: "",
      Description: "",
      Duration: "",
      Priority: "Medium",
      Status: "Not Started",
      DueDate: "",
      DateAssigned: formatDateForForm(new Date()), // Default to today
      Effort: "",
      Progress: 0,
      Notes: "",
      StartDate: formatDateForForm(new Date()),
      Initiative: "",  
      InitiatedBy: "", // Leave empty for user to fill in who initiated the task
    });
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    if (!currentUser || currentUser.trim() === '') {
      alert("Error: Unable to determine current user. Please refresh the page and try again.");
      console.error("No current user found - cannot create task");
      return;
    }
    
    try {
      // POST uses camelCase - task will be assigned to current user
      const createData = {
        title: formData.Title,
        description: formData.Description || null,
        duration: formData.Duration ? parseFloat(formData.Duration) : null,
        priority: formData.Priority,
        status: formData.Status,
        dueDate: formData.DueDate || null,
        dateAssigned: formData.DateAssigned || null,
        effort: formData.Effort ? parseFloat(formData.Effort) : null,
        progress: formData.Progress ? String(formData.Progress) : null,
        notes: formData.Notes || null,
        startDate: formData.StartDate || null,
        assignedTo: currentUser, // Always assign to current user
        initiatedBy: formData.InitiatedBy || null,
        initiative: formData.Initiative || null,
      };
      
      console.log("Creating task assigned to:", currentUser); // Debug log
      await createTask(createData);
      handleCloseCreateModal();
      fetchUserTasks(currentUser); // Refresh the task list
    } catch (err) {
      console.error("Error creating task:", err);
    }
  };

  // Calculate summary statistics
  const summaryStats = React.useMemo(() => {
    const allUserTasks = tasks;
    const overdue = allUserTasks.filter(t => {
      if (!t.DueDate || t.Status === "Completed") return false;
      return isPast(new Date(t.DueDate)) && !isToday(new Date(t.DueDate));
    }).length;
    
    const dueToday = allUserTasks.filter(t => {
      if (!t.DueDate || t.Status === "Completed") return false;
      return isToday(new Date(t.DueDate));
    }).length;

    const inProgress = allUserTasks.filter(t => t.Status === "In Progress").length;
    const notStarted = allUserTasks.filter(t => t.Status === "Not Started").length;
    const completed = allUserTasks.filter(t => t.Status === "Completed").length;

    return { overdue, dueToday, inProgress, notStarted, completed, total: allUserTasks.length };
  }, [tasks]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading your tasks...</p>
      </div>
    );
  }

  return (
    <div className="user-tasks-container">
      <div className="user-tasks-header d-flex justify-content-between align-items-center">
        <h2>My Engineering Tasks</h2>
        <Button variant="primary" onClick={handleOpenCreateModal}>
          <i className="bi bi-plus-circle"></i> Create New Task
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Summary Statistics */}
      <div className="d-flex gap-3 mb-3 flex-wrap">
        <Card style={{ minWidth: "150px" }}>
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.total}</h4>
            <small className="text-muted">Total Tasks</small>
          </Card.Body>
        </Card>
        <Card style={{ minWidth: "150px" }} bg="danger" text="white">
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.overdue}</h4>
            <small>Overdue</small>
          </Card.Body>
        </Card>
        <Card style={{ minWidth: "150px" }} bg="warning">
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.dueToday}</h4>
            <small>Due Today</small>
          </Card.Body>
        </Card>
        <Card style={{ minWidth: "150px" }} bg="primary" text="white">
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.inProgress}</h4>
            <small>In Progress</small>
          </Card.Body>
        </Card>
        <Card style={{ minWidth: "150px" }} bg="secondary" text="white">
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.notStarted}</h4>
            <small>Not Started</small>
          </Card.Body>
        </Card>
        <Card style={{ minWidth: "150px" }} bg="success" text="white">
          <Card.Body className="text-center p-2">
            <h4 className="mb-0">{summaryStats.completed}</h4>
            <small>Completed</small>
          </Card.Body>
        </Card>
      </div>

      <div className="task-filters mb-3">
        <Form.Group className="filter-group">
          <Form.Label>Sort By</Form.Label>
          <Form.Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="DueDate">Due Date</option>
            <option value="Priority">Priority</option>
            <option value="Progress">Progress</option>
            <option value="StartDate">Start Date</option>
            <option value="DateCreated">Date Created</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Order</Form.Label>
          <Form.Select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Priority</Form.Label>
          <Form.Select
            value={filters.priority || ""}
            onChange={(e) => setFilters({ ...filters, priority: e.target.value || null })}
          >
            <option value="">All Priorities</option>
            <option value="Low">Low</option>
            <option value="Medium">Medium</option>
            <option value="High">High</option>
            <option value="Critical">Critical</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Label>Status</Form.Label>
          <Form.Select
            value={filters.status || ""}
            onChange={(e) => setFilters({ ...filters, status: e.target.value || null })}
          >
            <option value="">All Statuses</option>
            <option value="Not Started">Not Started</option>
            <option value="In Progress">In Progress</option>
            <option value="On Hold">On Hold</option>
            <option value="Completed">Completed</option>
          </Form.Select>
        </Form.Group>

        <Form.Group className="filter-group">
          <Form.Check
            type="checkbox"
            label="Show Completed Tasks"
            checked={filters.showCompleted}
            onChange={(e) => setFilters({ ...filters, showCompleted: e.target.checked })}
          />
        </Form.Group>

        <Button 
          variant="outline-secondary" 
          onClick={() => setFilters({ priority: null, status: null, showCompleted: false })}
        >
          Reset Filters
        </Button>
      </div>

      {filteredAndSortedTasks.length === 0 ? (
        <Alert variant="info">
          <i className="bi bi-check-circle"></i> No tasks match your current filters.
        </Alert>
      ) : (
        <div className="tasks-list">
          {filteredAndSortedTasks.map(task => {
            const dueDateStatus = getDueDateStatus(task.DueDate);
            const isExpanded = expandedTask === task.ID;
            const commentCount = comments && comments[task.ID] ? comments[task.ID].filter(c => c.TaskID === task.ID).length : 0;

            return (
              <Card key={task.ID} className="task-card">
                <Card.Header className="task-card-header">
                  <div style={{ flex: 1 }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h5 className="mb-0">{task.Title}</h5>
                      {commentCount > 0 && (
                        <Badge bg="info" title={`${commentCount} comment${commentCount !== 1 ? 's' : ''}`}>
                          <i className="bi bi-chat-left-text"></i> {commentCount}
                        </Badge>
                      )}
                      {getPriorityBadge(task.Priority)}
                      {getStatusBadge(task.Status)}
                    </div>
                    <div className="task-meta">
                      <div className={`task-meta-item ${dueDateStatus.isOverdue ? 'overdue' : ''}`}>
                        <i className="bi bi-calendar"></i>
                        <span>{dueDateStatus.text}</span>
                      </div>
                      {task.Progress !== null && (
                        <div className="task-meta-item">
                          <i className="bi bi-bar-chart"></i>
                          <span>{task.Progress}% Complete</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="link"
                    onClick={() => toggleTaskExpansion(task.ID)}
                  >
                    {isExpanded ? (
                      <i className="bi bi-chevron-up"></i>
                    ) : (
                      <i className="bi bi-chevron-down"></i>
                    )}
                  </Button>
                </Card.Header>

                {isExpanded && (
                  <Card.Body className="task-card-body">
                    {task.Description && (
                      <div className="mb-3">
                        <strong>Description:</strong>
                        <p>{task.Description}</p>
                      </div>
                    )}

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <strong>Status:</strong>
                        <Form.Select
                          value={task.Status}
                          onChange={(e) => handleUpdateStatus(task.ID, e.target.value)}
                          className="mt-1"
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                          <option value="On Hold">On Hold</option>
                          <option value="Completed">Completed</option>
                        </Form.Select>
                      </div>
                      <div className="col-md-6">
                        <strong>Progress:</strong>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <Form.Range
                            value={task.Progress || 0}
                            onChange={(e) => handleUpdateProgress(task.ID, e.target.value)}
                            min="0"
                            max="100"
                            step="5"
                          />
                          <span style={{ minWidth: "50px" }}>{task.Progress || 0}%</span>
                        </div>
                      </div>
                    </div>

                    <div className="row mb-3">
                      {task.StartDate && (
                        <div className="col-md-6">
                          <strong>Start Date:</strong>
                          <p>{format(new Date(task.StartDate), "MMM dd, yyyy")}</p>
                        </div>
                      )}
                      {task.Duration && (
                        <div className="col-md-6">
                          <strong>Duration:</strong>
                          <p>{task.Duration} hours</p>
                        </div>
                      )}
                    </div>

                    {task.Notes && (
                      <div className="mb-3">
                        <strong>Notes:</strong>
                        <p className="text-muted">{task.Notes}</p>
                      </div>
                    )}

                    <div className="text-muted small mb-3">
                      <i className="bi bi-clock"></i> Created: {format(new Date(task.DateCreated), "MMM dd, yyyy")}
                    </div>

                    {/* Comments Section */}
                    <div className="mt-4 pt-3 border-top">
                      <h6 className="mb-3">
                        <i className="bi bi-chat-left-text"></i> Comments
                        {comments && comments[task.ID] && comments[task.ID].filter(c => c.TaskID === task.ID).length > 0 && (
                          <Badge bg="secondary" className="ms-2">
                            {comments[task.ID].filter(c => c.TaskID === task.ID).length}
                          </Badge>
                        )}
                      </h6>

                      {/* Existing Comments */}
                      <div className="comments-list mb-3">
                        {comments && comments[task.ID] && comments[task.ID].length > 0 ? (
                          comments[task.ID]
                            .filter(comment => comment.TaskID === task.ID)
                            .map((comment) => (
                            <div key={comment.ID} className="comment-item mb-2 p-2 bg-light rounded">
                              <div className="d-flex justify-content-between align-items-start">
                                <div className="flex-grow-1">
                                  <small className="text-muted">
                                    <strong>{comment.Author}</strong> • {format(new Date(comment.DateCreated), "MMM dd, yyyy 'at' h:mm a")}
                                  </small>
                                  <p className="mb-0 mt-1">{comment.Comment}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-muted small">No comments yet.</p>
                        )}
                      </div>

                      {/* Add Comment Form */}
                      <div className="add-comment-form">
                        <Form.Group>
                          <div className="d-flex gap-2">
                            <Form.Control
                              as="textarea"
                              rows={2}
                              placeholder="Add a comment..."
                              value={newComment[task.ID] || ""}
                              onChange={(e) => handleCommentChange(task.ID, e.target.value)}
                              disabled={commentLoading[task.ID]}
                            />
                            <Button
                              variant="primary"
                              onClick={() => handleAddComment(task.ID)}
                              disabled={!newComment[task.ID]?.trim() || commentLoading[task.ID]}
                              style={{ minWidth: "80px" }}
                            >
                              {commentLoading[task.ID] ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                "Post"
                              )}
                            </Button>
                          </div>
                        </Form.Group>
                      </div>
                    </div>
                  </Card.Body>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Task Modal */}
      <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Create New Task</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateTask}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title *</Form.Label>
              <Form.Control
                type="text"
                name="Title"
                value={formData.Title}
                onChange={handleInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
              />
            </Form.Group>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Priority</Form.Label>
                  <Form.Select
                    name="Priority"
                    value={formData.Priority}
                    onChange={handleInputChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="Status"
                    value={formData.Status}
                    onChange={handleInputChange}
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="StartDate"
                    value={formData.StartDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date Assigned</Form.Label>
                  <Form.Control
                    type="date"
                    name="DateAssigned"
                    value={formData.DateAssigned}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Due Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="DueDate"
                    value={formData.DueDate}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                {/* Empty column to maintain layout */}
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Initiated By</Form.Label>
                  <Form.Control
                    type="text"
                    name="InitiatedBy"
                    value={formData.InitiatedBy}
                    onChange={handleInputChange}
                    placeholder="Enter name..."
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Initiative</Form.Label>
                  <Form.Control
                    type="text"
                    name="Initiative"
                    value={formData.Initiative}
                    onChange={handleInputChange}
                    list="initiative-options"
                    placeholder="Enter initiative or select from existing..."
                  />
                  <datalist id="initiative-options">
                    {uniqueInitiatives.map((initiative) => (
                      <option key={initiative} value={initiative} />
                    ))}
                  </datalist>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Duration (hours)</Form.Label>
                  <Form.Control
                    type="number"
                    name="Duration"
                    step="0.5"
                    value={formData.Duration}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Effort (hours)</Form.Label>
                  <Form.Control
                    type="number"
                    name="Effort"
                    step="0.5"
                    value={formData.Effort}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Progress (%)</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Range
                  name="Progress"
                  min="0"
                  max="100"
                  step="5"
                  value={formData.Progress}
                  onChange={handleInputChange}
                />
                <span style={{ minWidth: "50px" }}>{formData.Progress}%</span>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="Notes"
                value={formData.Notes}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Alert variant="info" className="mb-3">
              <i className="bi bi-info-circle"></i> This task will be automatically assigned to you{currentUser ? ` (${currentUser})` : ""}. The "Initiated By" field is for tracking who originally requested this task.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCreateModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create Task
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default UserTasks;
